import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { concat, Observable, of, Subject, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Device } from '../app.types';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import {
  DeviceCommandResult,
  DeviceKey,
  DeviceLiveStatus,
  FirmwareStatus,
  HubEnvironment,
  IoTToolService,
} from '../services/iot-tool/iot-tool.service';
import { LogsRequestsService } from '../services/logs-requests/logs-requests.service';
import { DialogService } from '../services/dialog/dialog.service';
import { createDefaultData } from '../confirm-dialog/confirm-dialog.component';
import { ToastrService } from 'ngx-toastr';

interface SelectOption {
  key: string | number;
  value: string;
}

/** A recently selected device, kept for the quick-pick list beside the selector. */
interface RecentDevice {
  serial: string;
  /** Asset (fleet) id remembered so the chip still labels sensibly if the device later drops out of the list. */
  fleetId: string;
}

/** One row of a (possibly bulk) operation's outcome. */
interface OperationResultRow {
  serialNumber: string;
  success: boolean;
  detail: string;
  /** The device's response payload, when the command returned one (e.g. shell command output). */
  response?: string;
}

/** The most recent operation and its per-device results. */
interface OperationResult {
  operation: string;
  rows: OperationResultRow[];
  pending: boolean;
}

/**
 * Standalone "IoT Tool" tab. Supports multi-device selection: singular actions (commands, Teltonika
 * config, reassign, reveal keys, firmware status, device info/ping) require exactly one device;
 * bulk-capable actions (push config, download config, download logs) run against every selected
 * device independently, never stopping on the first failure, and report a per-device result.
 */
@Component({
  selector: 'app-iot-tool-page',
  templateUrl: './iot-tool-page.component.html',
  styleUrls: ['./iot-tool-page.component.scss'],
  standalone: false,
})
export class IoTToolPageComponent implements OnInit, OnDestroy {
  // T2000 firmware ships as either a .tar bundle or a raw .bin image. The backend is the authority.
  private static readonly allowedFirmwareExtensions = ['.tar', '.bin'];

  // Cap the multi-selection so bulk operations and the overview table stay manageable.
  private static readonly maxDevices = 25;
  get maxDevices(): number {
    return IoTToolPageComponent.maxDevices;
  }

  // Keep a short quick-pick list of the devices most recently added to the selection, persisted so it
  // survives a reload and makes jumping back to a device you were just working on a single click.
  private static readonly maxRecent = 5;
  private static readonly recentStorageKey = 'iotTool.recentDevices';
  /** Recently selected devices, newest first, for the quick-pick list beside the selector. */
  recentDevices: RecentDevice[] = [];

  // Configuration uploads are a raw .cfg or a .zip bundle. The backend is the authority.
  private static readonly allowedConfigExtensions = ['.cfg', '.zip'];

  get firmwareAccept(): string {
    return IoTToolPageComponent.allowedFirmwareExtensions.join(',');
  }

  get firmwareExtensionsLabel(): string {
    return IoTToolPageComponent.allowedFirmwareExtensions.join(' / ');
  }

  get configAccept(): string {
    return IoTToolPageComponent.allowedConfigExtensions.join(',');
  }

  get configExtensionsLabel(): string {
    return IoTToolPageComponent.allowedConfigExtensions.join(' / ');
  }

  // Teltonika target-type named values, from the legacy utility (2 is intentionally absent).
  readonly targetTypeOptions: SelectOption[] = [
    { key: -1, value: 'None' },
    { key: 0, value: 'T1600 (0)' },
    { key: 1, value: 'T1000 (1)' },
    { key: 3, value: 'V400 (3)' },
    { key: 4, value: 'X200 (4)' },
    { key: 5, value: 'V500 (5)' },
  ];

  // Common shell commands, as a quick-pick that fills the editable shell-command field. The key is the
  // exact command run on the unit (lower case — the device shell is case-sensitive); the label pairs a
  // plain-English purpose with the command so the operator can recognise it.
  readonly shellCommandPresets: SelectOption[] = [
    { key: 'smartctl -a /dev/sda', value: 'Device information — smartctl -a /dev/sda' },
    { key: 'uptime', value: 'Show the uptime of the device since last reboot — uptime' },
    { key: 'date', value: 'Date/time, to check the unit has synced — date' },
    { key: 'ps', value: 'List running processes — ps' },
    { key: 'df', value: 'File system disk usage — df' },
    { key: 'reboot', value: 'Reboot the device — reboot' },
  ];

  form: FormGroup;

  devices: Device[] = [];
  // synx-multi-select-dropdown uses { id, value } options and binds an array of selected ids.
  deviceOptions: { id: string; value: string }[] = [];
  firmwareOptions: SelectOption[] = [];
  configOptions: SelectOption[] = [];
  hubEnvironmentOptions: SelectOption[] = [];
  currentHub: HubEnvironment | null = null;

  /** The multi-selection of device serial numbers. */
  selectedSerials: string[] = [];

  // Live status keyed by serial number, so every selected device (single or in the overview) has its own.
  liveStatusMap: { [serial: string]: DeviceLiveStatus } = {};
  pingingSet: { [serial: string]: boolean } = {};

  firmwareUpload: File | null = null;
  configUpload: File | null = null;
  bulkPending = false;

  lastResult: OperationResult | null = null;
  deviceKey: DeviceKey | null = null;
  firmwareStatus: FirmwareStatus | null = null;

  private readonly destroyed$ = new Subject<void>();

  // Serials for which the target API key was successfully sent in THIS session. Drives the soft gate on
  // the move: if a unit's target API key hasn't been sent, Move asks the operator to acknowledge it first.
  // Session-local only — it can't know about keys pushed in a previous session or already correct units.
  private readonly apiKeySentSerials = new Set<string>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly iotTool: IoTToolService,
    private readonly deviceService: DeviceManagementService,
    private readonly logsRequests: LogsRequestsService,
    private readonly dialogService: DialogService,
    private readonly toastr: ToastrService,
    private readonly router: Router
  ) {
    this.form = this.fb.group({
      deviceSelection: [[]],
      simulateBitfield: [0],
      shellCommandPreset: [''],
      shellCommand: [''],
      gpsBatchSize: [1],
      emulatorTargetIp: [''],
      emulatorDataIp: [''],
      emulatorTargetType: [-1],
      emulatorOfflineTimeout: [5],
      hubTarget: [''],
      hubKey: [''],
      hubApiKey: [''],
      selectedFirmwareFile: [''],
      selectedConfigFile: [''],
    });
  }

  ngOnInit(): void {
    this.recentDevices = this.loadRecentDevices();
    this.loadDevices();
    this.loadFirmwareFiles();
    this.loadConfigFiles();
    this.loadHubEnvironments();
    this.loadCurrentHub();

    // The multi-select dropdown owns the selection; keep our derived state in sync with its value.
    this.form
      .get('deviceSelection')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((serials: string[]) => this.onSelectionValue(serials || []));

    // The preset dropdown is a quick-pick that fills the editable shell-command field, so the operator
    // can run a common command as-is or tweak it first. Reset the picker after it fills so the same
    // preset can be chosen again (and the dropdown returns to its placeholder).
    this.form
      .get('shellCommandPreset')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((command: string) => {
        if (command) {
          this.form.get('shellCommand').setValue(command);
          this.form.get('shellCommandPreset').setValue('', { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // ----- Selection model -----

  get selectedDevices(): Device[] {
    return this.selectedSerials
      .map((s) => this.devices.find((d) => d.serialNumber === s))
      .filter((d): d is Device => !!d);
  }

  /** Exactly one device selected — the precondition for singular actions. */
  get singleSelected(): boolean {
    return this.selectedSerials.length === 1;
  }

  get hasSelection(): boolean {
    return this.selectedSerials.length > 0;
  }

  /**
   * True when a device is a Teltonika router (the RUT955 / RUT956 family). Mirrors the Teltonika
   * branches of the backend HardwareTypeExtensions.GetHardwareTypeFromString over the health-reported
   * `hardware` string (which only arrives once the device has reported health). Teltonika units can't
   * return their configuration, so config-download is gated on this.
   */
  isTeltonika(device: Device | null): boolean {
    const hardware = (device?.hardware ?? '').toUpperCase();
    if (!hardware) return false;
    return (
      hardware === 'RUT955' ||
      hardware === 'RUT956' ||
      hardware.endsWith(' IOTV5') ||
      hardware.endsWith(' IOTV6') ||
      hardware === 'T1000_CONNECTED' ||
      hardware === 'T1600_CONNECTED'
    );
  }

  /** True when any selected device is a Teltonika — configuration can't be pulled off those. */
  get selectionHasTeltonika(): boolean {
    return this.selectedDevices.some((d) => this.isTeltonika(d));
  }

  /** The single selected device, or null when zero/many are selected. */
  get selectedDevice(): Device | null {
    return this.singleSelected ? this.devices.find((d) => d.serialNumber === this.selectedSerials[0]) ?? null : null;
  }

  private get serial(): string {
    return this.selectedSerials[0];
  }

  get assetId(): string {
    return this.selectedDevice?.fleetId || '—';
  }

  get hardwareId(): string {
    return this.selectedDevice?.deviceId || '—';
  }

  get hardwareType(): string {
    return this.selectedDevice?.hwVer || this.selectedDevice?.hardware || '—';
  }

  private onSelectionValue(serials: string[]): void {
    let next = serials;
    if (next.length > IoTToolPageComponent.maxDevices) {
      next = next.slice(0, IoTToolPageComponent.maxDevices);
      this.toastr.error(`You can select up to ${IoTToolPageComponent.maxDevices} devices.`, 'Limit reached');
      this.form.get('deviceSelection').setValue(next, { emitEvent: false });
    }
    const added = next.filter((s) => !this.selectedSerials.includes(s));
    this.selectedSerials = next;
    this.onSelectionChanged();
    added.forEach((s) => this.recordRecentDevice(s));
    added.forEach((s) => this.pingDevice(s));
  }

  removeDevice(serial: string): void {
    // Drive removal through the dropdown's control so its own state stays in sync.
    this.form.get('deviceSelection').setValue(this.selectedSerials.filter((s) => s !== serial));
  }

  clearSelection(): void {
    this.form.get('deviceSelection').setValue([]);
  }

  deviceLabel(serial: string): string {
    const d = this.devices.find((x) => x.serialNumber === serial);
    return d?.fleetId ? `${serial} (${d.fleetId})` : serial;
  }

  // ----- Recently selected devices (quick-pick) -----

  /** True when a serial is part of the current selection (used to mark a recent chip as already picked). */
  isSelected(serial: string): boolean {
    return this.selectedSerials.includes(serial);
  }

  /**
   * Label for a recent quick-pick chip: the live "serial (asset id)" while the device is still in the
   * loaded list, otherwise the serial with the asset id we remembered when it was selected.
   */
  recentDeviceLabel(recent: RecentDevice): string {
    if (this.devices.some((d) => d.serialNumber === recent.serial)) {
      return this.deviceLabel(recent.serial);
    }
    return recent.fleetId ? `${recent.serial} (${recent.fleetId})` : recent.serial;
  }

  /** Quick-pick a recently selected device, adding it to the current selection (a no-op when already selected). */
  selectRecent(serial: string): void {
    if (this.selectedSerials.includes(serial)) return;
    this.form.get('deviceSelection').setValue([...this.selectedSerials, serial]);
  }

  /** Forget the recently-selected quick-pick list. */
  clearRecentDevices(): void {
    this.recentDevices = [];
    this.saveRecentDevices();
  }

  /** Forget a single device from the quick-pick list, leaving the rest. */
  removeRecentDevice(serial: string): void {
    this.recentDevices = this.recentDevices.filter((r) => r.serial !== serial);
    this.saveRecentDevices();
  }

  /** Move a just-selected device to the top of the quick-pick list (deduped, newest first, capped). */
  private recordRecentDevice(serial: string): void {
    if (!serial) return;
    const fleetId = this.devices.find((d) => d.serialNumber === serial)?.fleetId ?? '';
    const withoutDuplicate = this.recentDevices.filter((r) => r.serial !== serial);
    this.recentDevices = [{ serial, fleetId }, ...withoutDuplicate].slice(0, IoTToolPageComponent.maxRecent);
    this.saveRecentDevices();
  }

  private loadRecentDevices(): RecentDevice[] {
    try {
      const raw = localStorage.getItem(IoTToolPageComponent.recentStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((r) => r && typeof r.serial === 'string')
        .map((r) => ({ serial: r.serial as string, fleetId: typeof r.fleetId === 'string' ? r.fleetId : '' }))
        .slice(0, IoTToolPageComponent.maxRecent);
    } catch {
      // Unreadable or malformed storage — start with an empty list rather than breaking the tab.
      return [];
    }
  }

  private saveRecentDevices(): void {
    try {
      localStorage.setItem(IoTToolPageComponent.recentStorageKey, JSON.stringify(this.recentDevices));
    } catch {
      // Storage unavailable (private mode / quota) — the quick-pick list just won't persist.
    }
  }

  /** Asset id (fleet id) for a serial number, shown alongside the serial in the results panel. */
  assetIdFor(serial: string): string {
    return this.devices.find((d) => d.serialNumber === serial)?.fleetId || '—';
  }

  /** Hardware id (device id) for a serial number, shown alongside the serial in the results panel. */
  hardwareIdFor(serial: string): string {
    return this.devices.find((d) => d.serialNumber === serial)?.deviceId || '—';
  }

  private onSelectionChanged(): void {
    // Device-specific reads (twin status / keys) are only meaningful for a single device.
    this.firmwareStatus = null;
    this.deviceKey = null;
  }

  // ----- Device list / info / live status -----

  loadDevices(): void {
    this.deviceService.getAllDevicesForExport({}).subscribe((devices) => {
      this.devices = Array.isArray(devices) ? devices : [];
      // The label carries serial number + asset id + hardware id so the dropdown's search matches any of them.
      this.deviceOptions = this.devices.map((d) => ({
        id: d.serialNumber,
        value: [d.serialNumber, d.fleetId, d.deviceId].filter((x) => !!x).join('  ·  '),
      }));
    });
  }

  reloadDevices(): void {
    this.loadDevices();
  }

  /** Firmware updates to devices are performed on the Asset Management page. */
  goToAssetManagement(): void {
    this.router.navigate(['/home/device-management']);
  }

  pingDevice(serial: string): void {
    if (!serial) return;
    this.pingingSet = { ...this.pingingSet, [serial]: true };
    this.iotTool.getLiveStatus(serial).subscribe((status) => {
      if (status) {
        this.liveStatusMap = { ...this.liveStatusMap, [serial]: status };
      }
      this.pingingSet = { ...this.pingingSet, [serial]: false };
    });
  }

  /** Re-ping every selected device (single "Re-ping" button and the overview refresh). */
  pingSelected(): void {
    this.selectedSerials.forEach((s) => this.pingDevice(s));
  }

  statusFor(serial: string): DeviceLiveStatus | null {
    return this.liveStatusMap[serial] ?? null;
  }

  isPinging(serial: string): boolean {
    return !!this.pingingSet[serial];
  }

  /** Hover text for a device's status dot: the status, plus the last-seen date when it's offline. */
  statusTooltip(serial: string): string {
    const st = this.statusFor(serial);
    if (!st) return '';
    if (st.indeterminate) return 'Unknown';
    if (st.online) return 'Online';
    const lastSeen = this.devices.find((d) => d.serialNumber === serial)?.lastPinged;
    // 'en-US' locale data is always registered by default; the explicit format gives the same output.
    return lastSeen ? `Offline, last seen ${formatDate(lastSeen, 'dd-MMM-yyyy HH:mm:ss', 'en-US')}` : 'Offline';
  }

  loadHubEnvironments(): void {
    this.iotTool.getHubEnvironments().subscribe((envs) => {
      this.hubEnvironmentOptions = (envs ?? []).map((e) => ({ key: e.hostName, value: e.name }));
    });
  }

  loadCurrentHub(): void {
    this.iotTool.getCurrentHub().subscribe((hub) => (this.currentHub = hub));
  }

  /** Target-environment options with the device's current environment removed (can't move it to itself). */
  get targetEnvironmentOptions(): SelectOption[] {
    const current = this.currentHub;
    if (!current) {
      return this.hubEnvironmentOptions;
    }
    return this.hubEnvironmentOptions.filter((o) => o.key !== current.hostName && o.value !== current.name);
  }

  // ----- Result plumbing -----

  private rowFrom(serial: string, r: DeviceCommandResult | null): OperationResultRow {
    if (!r) {
      return { serialNumber: serial, success: false, detail: 'Failed or no response' };
    }
    const detail = `Status ${r.status}` + (r.errorMessage ? ` — ${r.errorMessage}` : '');
    return {
      serialNumber: r.serialNumber || serial,
      success: r.successfullySent,
      detail,
      response: r.responsePayload || undefined,
    };
  }

  /**
   * Format a device response payload for display: a JSON-encoded string is shown as the raw string
   * (so multi-line output like `smartctl -a` keeps its line breaks), an object is pretty-printed, and
   * anything non-JSON is shown as-is.
   */
  formatResponse(response: string): string {
    try {
      const parsed = JSON.parse(response);
      if (parsed && typeof parsed === 'object') {
        // Command responses wrap the output as base64 in a "Data" field (e.g. shell command stdout),
        // so decode that to show the real text rather than the raw envelope.
        const data = parsed.Data ?? parsed.data;
        if (typeof data === 'string' && data.length) {
          const decoded = this.decodeBase64(data);
          if (decoded !== null) {
            return decoded;
          }
        }
        return JSON.stringify(parsed, null, 2);
      }
      return typeof parsed === 'string' ? parsed : String(parsed);
    } catch {
      return response;
    }
  }

  /** Decode a base64 string to UTF-8 text, or null if it is not valid base64. */
  private decodeBase64(value: string): string | null {
    try {
      const binary = atob(value);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch {
      return null;
    }
  }

  /** Run a singular command and record its result (the service already toasts success/failure). */
  private runSingle(operation: string, obs: Observable<DeviceCommandResult | null>): void {
    const serial = this.serial;
    this.lastResult = { operation, rows: [], pending: true };
    this.scrollToResults();
    obs.subscribe({
      next: (r) => {
        if (r) {
          this.lastResult = { operation, rows: [this.rowFrom(serial, r)], pending: false };
        }
      },
      complete: () => {
        if (this.lastResult && this.lastResult.pending) {
          this.lastResult = { ...this.lastResult, pending: false };
        }
      },
    });
  }

  /** Bring the results panel into view whenever an operation starts. */
  private scrollToResults(): void {
    setTimeout(() => {
      document.querySelector('.iot-tool__result-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /**
   * Run a bulk operation against several devices, appending each device's result to the panel as it
   * completes (rather than waiting for all of them), and never stopping on the first failure.
   * <p>sequential = true processes one device at a time so each result is rendered before the next
   * request starts (used for the fast config push, where parallel requests would otherwise land in the
   * same frame and appear all at once). The slower poll-based ops run in parallel.</p>
   */
  private runBulkStreaming(operation: string, perDevice: Observable<OperationResultRow>[], sequential = false): void {
    this.bulkPending = true;
    this.lastResult = { operation, rows: [], pending: true };
    this.scrollToResults();
    const total = perDevice.length;
    let done = 0;
    const onRow = (row: OperationResultRow) => {
      this.lastResult = { operation, rows: [...(this.lastResult?.rows ?? []), row], pending: done + 1 < total };
      done++;
      if (done === total) {
        this.bulkPending = false;
        const ok = this.lastResult.rows.filter((r) => r.success).length;
        if (ok === total) {
          this.toastr.success(`${operation}: ${ok}/${total} succeeded.`, 'Done');
        } else if (ok === 0) {
          this.toastr.error(`${operation}: all ${total} failed.`, 'Failed');
        } else {
          this.toastr.warning(`${operation}: ${ok} of ${total} succeeded.`, 'Partial');
        }
      }
    };

    if (total === 0) {
      this.bulkPending = false;
      this.lastResult = { operation, rows: [], pending: false };
      return;
    }

    if (sequential) {
      concat(...perDevice).subscribe(onRow);
    } else {
      perDevice.forEach((obs) => obs.subscribe(onRow));
    }
  }

  private requireSingle(): boolean {
    if (!this.singleSelected) {
      this.toastr.error('Select a single device for this operation.', 'One device required');
      return false;
    }
    return true;
  }

  private requireSelection(): boolean {
    if (!this.hasSelection) {
      this.toastr.error('Select at least one device.', 'No device selected');
      return false;
    }
    return true;
  }

  private confirmThen(message: string, action: () => void): void {
    this.dialogService.confirm(createDefaultData(message)).subscribe((result) => {
      if (result === true) {
        action();
      }
    });
  }

  // ----- Singular commands -----

  revertDefaults(): void {
    if (!this.requireSingle()) return;
    this.confirmThen(
      `Revert ${this.serial} to factory defaults? This is destructive and will disrupt the unit.`,
      () => this.runSingle('Revert to defaults', this.iotTool.revertDefaults(this.serial))
    );
  }

  simulateFailure(): void {
    if (!this.requireSingle()) return;
    const bitfield = Number(this.form.get('simulateBitfield').value);
    this.confirmThen(`Ask ${this.serial} to simulate failure(s) (bitfield ${bitfield})?`, () =>
      this.runSingle('Simulate failure', this.iotTool.simulateFailure(this.serial, bitfield))
    );
  }

  sendShellCommand(): void {
    if (!this.requireSingle()) return;
    const command = (this.form.get('shellCommand').value ?? '').toString();
    if (!command.trim()) {
      this.toastr.error('Enter a command to run.', 'No command');
      return;
    }
    this.confirmThen(`Run this shell command on ${this.serial}?\n\n${command}`, () =>
      this.runSingle('Shell command', this.iotTool.shellCommand(this.serial, command))
    );
  }

  updateGpsBatchSize(): void {
    if (!this.requireSingle()) return;
    const batchSize = Number(this.form.get('gpsBatchSize').value);
    if (batchSize <= 0) {
      this.toastr.error('Batch size must be greater than zero.', 'Invalid batch size');
      return;
    }
    this.runSingle('GPS batch size', this.iotTool.updateGpsBatchSize(this.serial, batchSize));
  }

  setEmulatorConfig(): void {
    if (!this.requireSingle()) return;
    const targetType = Number(this.form.get('emulatorTargetType').value);
    if (targetType < 0) {
      this.toastr.error('Choose a target type.', 'No target type');
      return;
    }
    this.runSingle(
      'Teltonika configuration',
      this.iotTool.setEmulatorConfig(
        this.serial,
        (this.form.get('emulatorTargetIp').value ?? '').toString(),
        (this.form.get('emulatorDataIp').value ?? '').toString(),
        targetType,
        Number(this.form.get('emulatorOfflineTimeout').value)
      )
    );
  }

  // ----- Reassign to another hub (singular) -----

  /**
   * Step 1 of a move: push the gaining environment's API key to the unit while it is still on THIS
   * (losing) hub, so it holds the right key the moment it arrives. The key is read from the gaining
   * environment. Kept separate from the move so the operator confirms it landed first.
   */
  sendApiKeyToTarget(): void {
    if (!this.requireSingle()) return;
    const apiKey = (this.form.get('hubApiKey').value ?? '').toString();
    if (!apiKey.trim()) {
      this.toastr.error("Enter the target environment's API key.", 'Missing details');
      return;
    }
    const serial = this.serial;
    this.confirmThen(
      `Send the target environment's API key to ${serial}?\n\n` +
        'Do this while the unit is still on this hub, BEFORE moving it. The unit must stay online for it to land.',
      () =>
        this.runSingle(
          'Send API key',
          this.iotTool.sendApiKey(serial, apiKey).pipe(
            // Remember a successful send so the move's soft gate knows the target API key has gone out.
            tap((r) => {
              if (r?.successfullySent) {
                this.apiKeySentSerials.add(serial);
              }
            })
          )
        )
    );
  }

  reassignHub(): void {
    if (!this.requireSingle()) return;
    const target = (this.form.get('hubTarget').value ?? '').toString();
    const key = (this.form.get('hubKey').value ?? '').toString();
    if (!target.trim() || !key.trim()) {
      this.toastr.error('Choose a target environment and enter the device key.', 'Missing details');
      return;
    }
    const serial = this.serial;
    const envName = this.hubEnvironmentOptions.find((o) => o.key === target)?.value ?? target;

    // Soft gate: if the target API key hasn't been sent in this session, make the operator acknowledge
    // that before the normal move confirmation — rather than blocking the move outright.
    if (!this.apiKeySentSerials.has(serial)) {
      this.confirmThen(
        `You haven't sent ${envName}'s API key to ${serial} in this session.\n\n` +
          `Without the target API key the unit won't authenticate in ${envName} after the move. ` +
          'Send it first (Step 1), or move anyway?',
        () => this.confirmAndMove(serial, target, key, envName)
      );
      return;
    }

    this.confirmAndMove(serial, target, key, envName);
  }

  /** The move confirmation + dispatch, reached directly or after the soft-gate acknowledgement. */
  private confirmAndMove(serial: string, target: string, key: string, envName: string): void {
    const currentEnvName = this.currentHub?.name ?? 'this environment';
    this.confirmThen(
      `Move ${serial} from ${currentEnvName} to ${envName}?\n\n` +
        `• The device key below must be ${envName}'s key — reveal it in ${envName}'s own IoT Tool, not this one.\n` +
        `• The unit must already be created in ${envName}.\n` +
        `• If the device key is wrong the unit will be unreachable in both environments and need manual recovery.`,
      () =>
        this.iotTool.updateDeviceHub(serial, target, key).subscribe((r) => {
          if (r) {
            this.lastResult = { operation: 'Move device', rows: [this.rowFrom(serial, r)], pending: false };
            this.scrollToResults();
            // The unit has moved off this hub, so its data here is now stale — refresh and reset.
            this.form.get('hubTarget').setValue('');
            this.form.get('hubKey').setValue('');
            this.form.get('hubApiKey').setValue('');
            this.apiKeySentSerials.delete(serial);
            this.clearSelection();
            this.loadDevices();
          }
        })
    );
  }

  // ----- Reads (singular) -----

  getFirmwareStatus(): void {
    if (!this.requireSingle()) return;
    this.iotTool.getFirmwareStatus(this.serial).subscribe((status) => (this.firmwareStatus = status));
  }

  /** Reveal the device keys (confirmed + audited) when hidden, or just hide them again when shown. */
  toggleDeviceKey(): void {
    if (this.deviceKey) {
      this.deviceKey = null;
      return;
    }
    this.getDeviceKey();
  }

  getDeviceKey(): void {
    if (!this.requireSingle()) return;
    this.confirmThen(`Reveal the IoT Hub keys for ${this.serial}? This is audited.`, () =>
      this.iotTool.getDeviceKey(this.serial).subscribe((key) => (this.deviceKey = key))
    );
  }

  // ----- Bulk: download logs (request + poll + download per device) -----

  downloadLogs(): void {
    if (!this.requireSelection()) return;
    const devices = this.selectedDevices.filter((d) => !!d.id);
    if (!devices.length) {
      this.toastr.error('None of the selected devices have an id on record.', 'Unavailable');
      return;
    }
    // Ask every device to upload its logs (fire once), then stream each download result as it's ready.
    // requestManyLogs emits more than once (HTTP events), so we don't chain the streaming off it.
    this.logsRequests.requestManyLogs(devices.map((d) => d.id)).subscribe();
    this.runBulkStreaming('Download logs', devices.map((d) => this.downloadLogsForDevice(d, 4)));
  }

  private downloadLogsForDevice(device: Device, attempts: number): Observable<OperationResultRow> {
    return this.iotTool.getDeviceLogs(device.id).pipe(
      switchMap((blob) => {
        if (blob) {
          this.triggerBlobDownload(blob, `logs_${this.safeName(device.serialNumber)}.zip`);
          return of<OperationResultRow>({ serialNumber: device.serialNumber, success: true, detail: 'Downloaded' });
        }
        if (attempts <= 1) {
          return of<OperationResultRow>({ serialNumber: device.serialNumber, success: false, detail: 'Not ready' });
        }
        return timer(3000).pipe(switchMap(() => this.downloadLogsForDevice(device, attempts - 1)));
      }),
      catchError(() => of<OperationResultRow>({ serialNumber: device.serialNumber, success: false, detail: 'Failed' }))
    );
  }

  // ----- Firmware (cloud storage; device-independent) -----

  loadFirmwareFiles(): void {
    this.iotTool.listFirmware().subscribe((files) => {
      this.firmwareOptions = (files ?? []).map((f) => ({ key: f, value: f }));
    });
  }

  onFirmwareSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.firmwareUpload = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  uploadFirmware(): void {
    if (!this.firmwareUpload) {
      this.toastr.error('Choose a firmware file first.', 'No file');
      return;
    }
    const name = this.firmwareUpload.name.toLowerCase();
    if (!IoTToolPageComponent.allowedFirmwareExtensions.some((ext) => name.endsWith(ext))) {
      this.toastr.error(
        `Only ${IoTToolPageComponent.allowedFirmwareExtensions.join(' / ')} firmware files are allowed.`,
        'Wrong file type'
      );
      return;
    }
    this.iotTool.uploadFirmware(this.firmwareUpload).subscribe((result) => {
      if (result) {
        this.firmwareUpload = null;
        this.loadFirmwareFiles();
      }
    });
  }

  deleteFirmware(): void {
    const fileName = (this.form.get('selectedFirmwareFile').value ?? '').toString();
    if (!fileName) {
      this.toastr.error('Select a firmware file to delete.', 'No file');
      return;
    }
    this.confirmThen(`Delete firmware '${fileName}' from cloud storage? This cannot be undone.`, () =>
      this.iotTool.deleteFirmware(fileName).subscribe((result) => {
        if (result) {
          this.form.get('selectedFirmwareFile').setValue('');
          this.loadFirmwareFiles();
        }
      })
    );
  }

  // ----- Configuration (upload is device-independent; push/download are bulk) -----

  loadConfigFiles(): void {
    this.iotTool.listConfigFiles().subscribe((files) => {
      this.configOptions = (files ?? []).map((f) => ({ key: f, value: f }));
    });
  }

  onConfigSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.configUpload = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  uploadConfig(): void {
    if (!this.configUpload) {
      this.toastr.error('Choose a configuration file first.', 'No file');
      return;
    }
    const name = this.configUpload.name.toLowerCase();
    if (!IoTToolPageComponent.allowedConfigExtensions.some((ext) => name.endsWith(ext))) {
      this.toastr.error(
        `Only ${IoTToolPageComponent.allowedConfigExtensions.join(' / ')} configuration files are allowed.`,
        'Wrong file type'
      );
      return;
    }
    this.iotTool.uploadConfig(this.configUpload).subscribe((result) => {
      if (result) {
        this.configUpload = null;
        this.loadConfigFiles();
      }
    });
  }

  pushConfig(): void {
    if (!this.requireSelection()) return;
    const fileName = (this.form.get('selectedConfigFile').value ?? '').toString();
    if (!fileName) {
      this.toastr.error('Select a configuration file to push.', 'No file');
      return;
    }
    const serials = [...this.selectedSerials];
    this.runBulkStreaming(
      'Push configuration',
      serials.map((s) =>
        this.iotTool.pushConfigQuiet(s, fileName).pipe(
          map((r) => this.rowFrom(s, r)),
          catchError(() => of<OperationResultRow>({ serialNumber: s, success: false, detail: 'Failed' }))
        )
      ),
      true // sequential: render each device's push result as it completes
    );
  }

  downloadConfig(): void {
    if (!this.requireSelection()) return;
    const serials = [...this.selectedSerials];
    this.runBulkStreaming(
      'Download configuration',
      serials.map((s) => this.downloadConfigForDevice(s, 4))
    );
  }

  private downloadConfigForDevice(serial: string, attempts: number): Observable<OperationResultRow> {
    return this.iotTool.pullConfigQuiet(serial).pipe(
      switchMap((pull) => {
        if (!pull) {
          return of<OperationResultRow>({ serialNumber: serial, success: false, detail: 'Request failed' });
        }
        return this.pollConfigForDevice(serial, pull.fileName, attempts);
      }),
      catchError(() => of<OperationResultRow>({ serialNumber: serial, success: false, detail: 'Failed' }))
    );
  }

  private pollConfigForDevice(serial: string, fileName: string, attempts: number): Observable<OperationResultRow> {
    return this.iotTool.downloadConfigFile(fileName).pipe(
      switchMap((blob) => {
        if (blob) {
          this.triggerBlobDownload(blob, `${this.safeName(serial)}_${fileName}`);
          return of<OperationResultRow>({ serialNumber: serial, success: true, detail: 'Downloaded' });
        }
        if (attempts <= 1) {
          return of<OperationResultRow>({ serialNumber: serial, success: false, detail: 'Not ready' });
        }
        return timer(3000).pipe(switchMap(() => this.pollConfigForDevice(serial, fileName, attempts - 1)));
      })
    );
  }

  private safeName(serial: string): string {
    return serial.replace(/[^a-zA-Z0-9._-]+/g, '-');
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }
}
